import { createBrowserRouter, RouterProvider } from 'react-router';
import { AriaRouterProvider } from './providers/aria-router-provider';
import { ThemeProvider } from './providers/theme-provider';
import { QueryProvider } from './providers/query-provider';
import { SiteContentGate } from './providers/site-content-gate';
import { setLogLevel } from '@personal-portfolio/common-utils';
import { VercelAnalytics } from '@/services/analytics/vercel-analytics';

import { Layout } from './layout/layout';
import { AboutPage } from '@/features/about/about-page';
import { ExperiencePage } from '@/features/experience/experience-page';
import { ProjectsPage } from '@/features/projects/projects-page';
import { EducationPage } from '@/features/education/education-page';
import { CertificationsPage } from '@/features/certifications/certifications-page';
import { AwardsPage } from '@/features/awards/awards-page';
import { FirebaseAnalyticsListener } from '@/services/analytics/firebase-analytics-listener';
import { NotFoundPage, RouteErrorPage } from './error-pages';
import { appConfig } from './config';

// Module scope, not inside App(): setLogLevel mutates a process-global
// loglevel singleton, and modules imported below (and their own module-scope
// code) may log before any component renders. Doing this in an effect would
// leave those early logs at loglevel's default level.
setLogLevel(appConfig.isDev ? 'development' : 'production');

const router = createBrowserRouter([
  {
    path: '/',
    // AriaRouterProvider lives here, not around <RouterProvider> below: it
    // calls useNavigate, which needs router context to exist already.
    element: (
      <AriaRouterProvider>
        <Layout />
        <FirebaseAnalyticsListener />
      </AriaRouterProvider>
    ),
    // Root-level fallback for errors thrown outside the Layout's Outlet
    // (e.g. in the Layout itself).
    errorElement: <RouteErrorPage />,
    children: [
      {
        // Pathless wrapper so page errors render inside the Layout chrome.
        errorElement: <RouteErrorPage />,
        children: [
          // `/` and `/about` deliberately render the same page rather than
          // one redirecting to the other, so an inbound link to either works.
          // AboutPage emits a canonical link pointing at `/` so search engines
          // still see a single URL — see the note in about-page.tsx.
          {
            index: true,
            element: <AboutPage />,
          },
          {
            path: 'about',
            element: <AboutPage />,
          },
          {
            path: 'experience',
            element: <ExperiencePage />,
          },
          {
            path: 'projects',
            element: <ProjectsPage />,
          },
          {
            path: 'education',
            element: <EducationPage />,
          },
          {
            path: 'certifications',
            element: <CertificationsPage />,
          },
          {
            path: 'awards',
            element: <AwardsPage />,
          },
          {
            path: '*',
            element: <NotFoundPage />,
          },
        ],
      },
    ],
  },
]);

export function App() {
  return (
    <QueryProvider>
      <ThemeProvider defaultTheme="system" storageKey="portfolio-web-ui-theme">
        {/* Copy has no bundled fallback, so nothing renders until the CMS
            answers — or immediately, from a restored cache. */}
        <SiteContentGate>
          <RouterProvider router={router} />
        </SiteContentGate>
      </ThemeProvider>
      <VercelAnalytics />
    </QueryProvider>
  );
}
